import { useState } from 'react';
import { z } from 'zod';
import type {
  CreateKeuringCommand,
  KeuringDto,
  PhaseId,
  UpdateKeuringCommand,
} from '@/api/generated';
import {
  useAttestKeuring,
  useCreateKeuring,
  useProject,
  useSeedStandardKeuringen,
  useUpdateKeuring,
} from '@/api/hooks';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import { formatShortDate } from '@/lib/format';
import { useFileUpload } from '@/lib/useFileUpload';
import styles from './KeuringDialogs.module.css';

const PHASE_OPTIONS: Array<{ id: PhaseId; label: string }> = [
  { id: 'Voor', label: 'Voorbereiding' },
  { id: 'Ontw', label: 'Ontwerp' },
  { id: 'Verg', label: 'Vergunningen' },
  { id: 'Aanb', label: 'Aanbesteding' },
  { id: 'Ruw', label: 'Ruwbouw' },
  { id: 'Wwd', label: 'Wind-/waterdicht' },
  { id: 'Tech', label: 'Technieken' },
  { id: 'Afw', label: 'Afwerking' },
  { id: 'Opl', label: 'Oplevering' },
  { id: 'Naz', label: 'Nazorg' },
];

const schema = z.object({
  name: z.string().trim().min(2, 'Minstens 2 tekens'),
  required: z.boolean(),
  date: z.string().optional().or(z.literal('')),
  dueDate: z.string().optional().or(z.literal('')),
  phaseId: z.string().min(1, 'Kies een fase'),
});

type KeuringFormValues = z.infer<typeof schema>;

function FieldsBody() {
  return (
    <>
      <FormField<KeuringFormValues> name="name" label="Naam" required>
        {(field) => (
          <Input
            {...field}
            value={(field.value as string | undefined) ?? ''}
            autoFocus
            placeholder="Bv. Elektrische keuring"
          />
        )}
      </FormField>
      <FormField<KeuringFormValues> name="required" label="Wettelijk verplicht">
        {(field) => (
          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              ref={field.ref}
              checked={Boolean(field.value)}
              onChange={(e) => field.onChange(e.target.checked)}
              onBlur={field.onBlur}
            />
            <span>Wettelijke verplichting (anders: aanbevolen)</span>
          </label>
        )}
      </FormField>
      <FormField<KeuringFormValues> name="phaseId" label="Fase" required>
        {(field) => (
          <select
            {...field}
            className={styles.select}
            value={(field.value as string) ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
          >
            {PHASE_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        )}
      </FormField>
      <FormField<KeuringFormValues>
        name="date"
        label="Uitgevoerd op"
        hint="Optioneel — vul in zodra de keuring afgerond is"
      >
        {(field) => (
          <Input
            {...field}
            value={(field.value as string) ?? ''}
            type="date"
            onChange={(e) => field.onChange(e.target.value)}
          />
        )}
      </FormField>
      <FormField<KeuringFormValues>
        name="dueDate"
        label="Wettelijke / geplande deadline"
        hint="Optioneel — leeg laten als nog niet bepaald"
      >
        {(field) => (
          <Input
            {...field}
            value={(field.value as string) ?? ''}
            type="date"
            onChange={(e) => field.onChange(e.target.value)}
          />
        )}
      </FormField>
    </>
  );
}

export function KeuringCreateDialog({
  open,
  onOpenChange,
  defaultPhaseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPhaseId?: PhaseId;
}) {
  const createMutation = useCreateKeuring();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Keuring inplannen</DialogTitle>
        <DialogDescription>Voeg een nieuwe keuring of attest-verplichting toe.</DialogDescription>
        <DialogBody>
          <Form<KeuringFormValues>
            schema={schema}
            defaultValues={{
              name: '',
              required: true,
              date: '',
              dueDate: '',
              phaseId: defaultPhaseId ?? 'Opl',
            }}
            onSubmit={async (values) => {
              const body: CreateKeuringCommand = {
                name: values.name.trim(),
                required: values.required,
                phaseId: values.phaseId,
                date: values.date && values.date.trim().length > 0 ? values.date : null,
                dueDate: values.dueDate && values.dueDate.trim().length > 0 ? values.dueDate : null,
              };
              await createMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Toevoegen</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export function KeuringEditDialog({
  open,
  onOpenChange,
  keuring,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keuring: KeuringDto;
}) {
  const updateMutation = useUpdateKeuring(keuring.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Keuring bewerken</DialogTitle>
        <DialogDescription>Pas naam, fase, datum of verplichting aan.</DialogDescription>
        <DialogBody>
          <Form<KeuringFormValues>
            schema={schema}
            defaultValues={{
              name: keuring.name,
              required: keuring.required,
              date: (keuring.date ?? '').slice(0, 10),
              dueDate: (keuring.dueDate ?? '').slice(0, 10),
              phaseId: keuring.phaseId,
            }}
            onSubmit={async (values) => {
              const body: UpdateKeuringCommand = {
                name: values.name.trim() || null,
                required: values.required,
                status: null,
                phaseId: values.phaseId,
                date: values.date && values.date.trim().length > 0 ? values.date : null,
                dueDate: values.dueDate && values.dueDate.trim().length > 0 ? values.dueDate : null,
              };
              await updateMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Opslaan</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export function KeuringAttestDialog({
  open,
  onOpenChange,
  keuring,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keuring: KeuringDto;
}) {
  const attest = useAttestKeuring();
  const picker = useFileUpload({ accept: 'application/pdf', multiple: false });
  const [busy, setBusy] = useState(false);

  const close = (next: boolean) => {
    if (!next) picker.reset();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    const file = picker.files[0];
    if (!file) return;
    setBusy(true);
    try {
      await attest.mutateAsync({ id: keuring.id, file });
      close(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogTitle>Attest uploaden — {keuring.name}</DialogTitle>
        <DialogDescription>
          Upload het ondertekende PDF-attest. De keuring komt automatisch op "afgerond".
        </DialogDescription>
        <DialogBody>
          <input {...picker.inputProps} />
          <button
            type="button"
            {...picker.dropZoneProps}
            onClick={picker.openPicker}
            className={`${styles.dropZone} ${picker.isDragging ? styles.dropZoneActive : ''}`}
            disabled={busy}
          >
            <Icon name="upload" size={18} />{' '}
            <span>{picker.files.length === 0 ? 'Selecteer PDF-attest' : picker.files[0].name}</span>
          </button>
          {picker.error && (
            <div role="alert" className={styles.error}>
              {picker.error}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Btn type="button" disabled={busy}>
              Annuleer
            </Btn>
          </DialogClose>
          <Btn
            variant="primary"
            onClick={handleSubmit}
            disabled={busy || picker.files.length === 0}
          >
            {busy ? 'Bezig…' : 'Uploaden'}
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SEED_PREVIEW: ReadonlyArray<{
  name: string;
  phaseLabel: string;
  rule: (start: Date | null, end: Date | null) => Date | null;
}> = [
  {
    name: 'Veiligheidscoördinatie - ontwerp',
    phaseLabel: 'Voorbereiding',
    rule: (s) => (s ? addDays(s, -30) : null),
  },
  {
    name: 'Ventilatievoorontwerp (VVO)',
    phaseLabel: 'Voorbereiding',
    rule: (s) => (s ? addDays(s, -14) : null),
  },
  {
    name: 'EPB-startverklaring',
    phaseLabel: 'Vergunningen',
    rule: (s) => (s ? addDays(s, -8) : null),
  },
  { name: 'Veiligheidscoördinatie - verwezenlijking', phaseLabel: 'Ruwbouw', rule: (_, e) => e },
  {
    name: 'Keuring privéwaterafvoer (riolering)',
    phaseLabel: 'Technieken',
    rule: (_, e) => (e ? addDays(e, -60) : null),
  },
  {
    name: 'Keuring binneninstallatie drinkwater',
    phaseLabel: 'Technieken',
    rule: (_, e) => (e ? addDays(e, -60) : null),
  },
  {
    name: 'Keuring elektrische installatie (AREI)',
    phaseLabel: 'Technieken',
    rule: (_, e) => (e ? addDays(e, -30) : null),
  },
  {
    name: 'Ventilatieprestatieverslag (VPV)',
    phaseLabel: 'Technieken',
    rule: (_, e) => (e ? addDays(e, -30) : null),
  },
  {
    name: 'EPB-aangifte (eindverklaring)',
    phaseLabel: 'Oplevering',
    rule: (_, e) => (e ? addMonths(e, 12) : null),
  },
  { name: 'Postinterventiedossier (PID)', phaseLabel: 'Oplevering', rule: (_, e) => e },
];

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function addMonths(d: Date, n: number): Date {
  const r = new Date(d);
  r.setMonth(r.getMonth() + n);
  return r;
}

function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  const trimmed = value.slice(0, 10);
  const date = new Date(`${trimmed}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function KeuringSeedDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const projectQuery = useProject();
  const seedMutation = useSeedStandardKeuringen(projectQuery.data?.id ?? '');

  const start = parseDateOnly(projectQuery.data?.startDate ?? null);
  const end = parseDateOnly(projectQuery.data?.endDate ?? null);
  const missingDates = !start || !end;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Belgische standaardlijst toevoegen?</DialogTitle>
        <DialogDescription>
          Dit voegt 10 wettelijke en aanbevolen keuringen toe voor een Vlaamse nieuwbouw. Geplande
          datums worden berekend op basis van je start- en einddatum. Reeds bestaande items met
          dezelfde naam worden niet gedupliceerd.
        </DialogDescription>
        <DialogBody>
          {missingDates && (
            <div className={styles.seedWarning} role="note">
              Tip: vul eerst de start- en einddatum in via Instellingen → Project, anders blijven
              sommige deadlines leeg.
            </div>
          )}
          <ul className={styles.seedPreview}>
            {SEED_PREVIEW.map((item) => {
              const due = item.rule(start, end);
              return (
                <li key={item.name}>
                  <span className={styles.seedPreviewName}>{item.name}</span>
                  <span className={styles.seedPreviewMeta}>
                    {item.phaseLabel}
                    {due
                      ? ` · deadline ${formatShortDate(due.toISOString().slice(0, 10))}`
                      : ' · datum onbekend'}
                  </span>
                </li>
              );
            })}
          </ul>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Btn type="button" disabled={seedMutation.isPending}>
              Annuleren
            </Btn>
          </DialogClose>
          <Btn
            variant="primary"
            disabled={seedMutation.isPending || !projectQuery.data}
            onClick={async () => {
              await seedMutation.mutateAsync();
              onOpenChange(false);
            }}
          >
            {seedMutation.isPending ? 'Bezig…' : 'Toevoegen'}
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
